import type { LucideIcon } from 'lucide-react';
import { ListChecks, Mail, UserCheck } from 'lucide-react';

export type AuthAside = {
  heading: string;
  lede?: string | undefined;
  card?: { title: string; points: { icon: LucideIcon; text: string }[] } | undefined;
};

const DEFAULT_AUTH_ASIDE: AuthAside = {
  heading: 'Remote roles, matched by people.',
  lede: 'Rules rank the pool. A human curator confirms every fit.',
  card: {
    title: 'How it works',
    points: [
      { icon: ListChecks, text: 'Clear, written rules rank every profile.' },
      { icon: UserCheck, text: 'Every fit is confirmed by a human curator.' },
      { icon: Mail, text: 'Nothing is shared without a yes.' },
    ],
  },
};

/**
 * Dark brand panel on every sign-in / sign-up page. Rendered by AuthLayout
 * (dashboards) and statically by apps/web layouts/AuthShell.astro (no
 * hydration, so it ships no JS on the public site). Below lg it stacks under
 * the form; from lg it is the sticky right column, at least viewport height
 * but free to grow, so short windows scroll the page instead of clipping it.
 * The logo sits in the flow (not absolute) so it can never overlap the card;
 * a matching spacer above keeps the content optically centred.
 * Expects /assets/remote-medium-logo-reversed.svg in the host app's public dir.
 */
export function AuthPanel({ aside = DEFAULT_AUTH_ASIDE }: { aside?: AuthAside | undefined }) {
  return (
    <aside className="mx-4 mb-4 flex flex-col overflow-hidden rounded-2xl bg-ink-surface px-6 py-10 text-ink-surface-foreground sm:mx-8 sm:px-10 lg:sticky lg:top-4 lg:m-0 lg:min-h-[calc(100dvh-2rem)] lg:self-start lg:px-14 lg:py-10">
      <div aria-hidden="true" className="hidden h-16 shrink-0 lg:block" />
      <div className="max-w-[480px] lg:my-auto lg:py-12">
        <p className="m-0 text-xs font-semibold tracking-widest text-ink-surface-muted-foreground uppercase">
          Remote Medium
        </p>
        <p className="mt-4 mb-0 font-serif text-3xl leading-tight font-medium tracking-tight lg:text-4xl xl:text-5xl">
          {aside.heading}
        </p>
        {aside.lede && (
          <p className="mt-5 mb-0 text-base leading-6 text-ink-surface-muted-foreground">
            {aside.lede}
          </p>
        )}
        {aside.card && (
          <div className="mt-8 rounded-xl border border-ink-surface-foreground/10 bg-ink-surface-foreground/5 p-6 lg:mt-10 lg:p-8">
            <p className="m-0 font-serif text-xl leading-tight font-medium tracking-tight lg:text-2xl">
              {aside.card.title}
            </p>
            <ul className="mt-5 mb-0 grid list-none gap-3 p-0 text-base leading-6 lg:mt-6 lg:gap-4">
              {aside.card.points.map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-start gap-3">
                  <Icon aria-hidden="true" className="mt-1 size-4 shrink-0 text-chart-4" />
                  {text}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <img
        src="/assets/remote-medium-logo-reversed.svg"
        alt=""
        width={53}
        height={64}
        className="mt-8 block h-12 w-auto shrink-0 self-end lg:mt-0 lg:h-16"
      />
    </aside>
  );
}
